import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { STACK_FEATURES } from './stackConfig'
import {
  canGoBackInStack,
  createStackEntryFromLocation,
  createStackEntryFromPath,
  isSameLocation,
  loadStackFromStorage,
  pushToStack,
  removeFeatureFromStack,
  saveStackToStorage,
  syncStackWithLocation,
  type PushOptions,
  type StackApi,
  type StackFeature
} from './stack'

/**
 * In-app back stack (separate from browser history). See README.md in this folder.
 *
 * useStack(): push, replace, pop (same-flow only), resetFeature.
 */
export type { PushOptions, StackApi, StackEntry, StackFeature } from './stack'
export { STACK_FEATURE, STACK_FEATURES } from './stackConfig'

const StackContext = createContext<StackApi | undefined>(undefined)

export function useStack(): StackApi {
  const stack = useContext(StackContext)
  if (!stack) {
    throw new Error('useStack must be used within a StackProvider')
  }
  return stack
}

type StackProviderProps = {
  children: ReactNode
  features?: readonly StackFeature[]
}

export function StackProvider({
  children,
  features = STACK_FEATURES
}: StackProviderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  // push/pop already wrote the stack, then they change the URL. The location
  // effect would otherwise treat that URL change as a refresh and rebuild the
  // stack (ghost frames). Ignore that one location update.
  const ignoreNextLocationChangeRef = useRef(false)

  const [entries, setEntries] = useState(() =>
    loadStackFromStorage(
      createStackEntryFromLocation(location.pathname, location.search ?? '', features),
      features
    )
  )

  // push/pop read this so they see the latest stack in the same tick as setState.
  const entriesRef = useRef(entries)
  entriesRef.current = entries

  useEffect(() => {
    saveStackToStorage(entries)
  }, [entries])

  useEffect(() => {
    const pathname = location.pathname
    const search = location.search ?? ''

    if (ignoreNextLocationChangeRef.current) {
      ignoreNextLocationChangeRef.current = false
      const topEntry = entriesRef.current[entriesRef.current.length - 1]
      // Our navigate landed on the URL we already stored. Do not rebuild.
      if (topEntry && isSameLocation(topEntry, pathname, search)) return
    }

    // URL changed without our push/pop (refresh, deep link, browser back).
    setEntries((currentEntries) =>
      syncStackWithLocation(currentEntries, pathname, search, features)
    )
  }, [location.pathname, location.search, features])

  const commitStackAndNavigate = useCallback(
    (nextEntries: typeof entries, path: string, replace: boolean) => {
      // Set the flag before navigate so the location effect does not rebuild.
      ignoreNextLocationChangeRef.current = true
      setEntries(nextEntries)
      navigate(path, { replace })
    },
    [navigate]
  )

  const push = useCallback(
    (path: string, options?: PushOptions) => {
      const replace = options?.replace ?? false
      const nextEntry = createStackEntryFromPath(path, features, options?.state ?? {})
      const nextEntries = pushToStack(entriesRef.current, nextEntry, replace, features)
      commitStackAndNavigate(nextEntries, path, replace)
    },
    [commitStackAndNavigate, features]
  )

  const replace = useCallback(
    (path: string, state?: Record<string, unknown>) => {
      push(path, { replace: true, state })
    },
    [push]
  )

  const pop = useCallback(() => {
    const currentEntries = entriesRef.current
    if (!canGoBackInStack(currentEntries)) return

    const nextEntries = currentEntries.slice(0, -1)
    const previousEntry = nextEntries[nextEntries.length - 1]
    if (!previousEntry) return

    // replace: true so the browser history does not grow; product Back owns this.
    commitStackAndNavigate(nextEntries, previousEntry.pathname + previousEntry.search, true)
  }, [commitStackAndNavigate])

  const resetFeature = useCallback((featureId: string, then?: () => void) => {
    const nextEntries = removeFeatureFromStack(entriesRef.current, featureId)
    entriesRef.current = nextEntries
    setEntries(nextEntries)
    // After this render so a following push/native-back sees the empty flow,
    // and so location-sync cannot put the current URL back on the stack.
    if (then) queueMicrotask(then)
  }, [])

  const current = entries[entries.length - 1] ?? null
  const previous = entries.length >= 2 ? entries[entries.length - 2] ?? null : null
  const canGoBack = canGoBackInStack(entries)

  const value = useMemo(
    () => ({
      entries,
      current,
      previous,
      canGoBack,
      push,
      replace,
      pop,
      resetFeature
    }),
    [entries, current, previous, canGoBack, push, replace, pop, resetFeature]
  )

  return <StackContext.Provider value={value}>{children}</StackContext.Provider>
}
